import { FC, useState } from 'react';
import {
  Root,
  Trigger,
  Portal,
  Content,
  Item,
} from '@radix-ui/react-dropdown-menu';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Icons, LineMargin } from '../enums';
import { Line, Icon } from '@/components';


const StyledTrigger = styled(Trigger) <{
  $rounded?: boolean;
  $largeImg?: boolean;
  $hasText?: boolean;
}>`
  ${tw`flex leading-none gap-3 items-center relative select-none outline-none cursor-pointer`};

  ${({ $rounded, $hasText }) =>
    $rounded && !$hasText ? tw`rounded-full` : tw`rounded-lg w-full`}
  ${({ $largeImg }) => ($largeImg ? tw`p-1` : tw`p-2`)}
  
  &:active {
    ${tw`outline outline-slate-800/20 outline-none outline-offset-2`};
  }

  &:hover {
    ${({ $hasText }) => $hasText && tw`bg-white/70`}
  }
`;

const Text = styled.span`
  ${tw`p-3 pl-0 text-sm`};
`;

const StyledIcon = styled(Icon)`
  ${tw`rounded-full`};
`;

const StyledContent = styled(Content)`
  ${tw`min-w-[220px] bg-white/90 text-sm font-normal backdrop-blur border border-white/90 rounded-xl p-1 shadow-2xl will-change-[opacity,transform]`};
  z-index: 100;
`;

const StyledItem = styled(Item)`
  ${tw`p-0.5`};

  &:focus-visible {
    ${tw`outline outline-slate-800/20 rounded-lg`};
  }

  button,
  a {
    ${tw`relative flex items-center w-full gap-2 p-3 text-slate-600 leading-none rounded-lg font-light select-none outline-none cursor-pointer`};

    &:hover {
      ${tw`bg-slate-800 text-slate-50 border-none`};
    }
  }
`;

const StyledImage = styled.img<{
  $size: 'small' | 'medium' | 'large';
  $rounded?: boolean;
}>`
  ${tw`drop-shadow-sm`};

  ${({ $size }) => {
    if ($size === 'small') return tw`h-8 w-8`;
    if ($size === 'medium') return tw`h-10 w-10`;
    else return tw`h-12 w-12`;
  }}
  ${({ $rounded }) => $rounded && tw`rounded-full`};
`;

interface Item {
  text?: string;
  img?: string;
  icon?: Icons;
  alt?: string;
  link?: string;
  handleClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  rounded?: boolean;
}

interface TriggerItem {
  text?: string;
  element?: JSX.Element;
  activeElement?: JSX.Element;
  img?: string;
  icon?: Icons;
  alt?: string;
  link?: string;
  handleClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  rounded?: boolean;
}

interface Props {
  data: { trigger: TriggerItem; items: Item[] };
  bottomLine?: boolean;
  imgSize?: 'small' | 'medium' | 'large';
}


const DropDown: FC<Props> = ({
  data: { trigger, items },
  bottomLine,
  imgSize,
}) => {
  const [isOpen,setIsOpen] = useState(false);
  return (
    <Root onOpenChange={setIsOpen}>
      {trigger.element ? (
        <Trigger>
          {isOpen ? trigger.activeElement : trigger.element}
        </Trigger>
      ) : (
        <StyledTrigger
          $rounded={trigger.rounded}
          $largeImg={trigger.img && imgSize === 'large' ? true : false}
          $hasText={trigger.text ? true : false}>
          {trigger.img && (
            <StyledImage
              src={trigger.img}
              alt={trigger.alt}
              $rounded={trigger.rounded}
              $size={imgSize || 'large'}
            />
          )}
          {trigger.icon && <StyledIcon icon={trigger.icon} />}
          {trigger.text && <Text>{trigger.text}</Text>}
        </StyledTrigger>
      )}
      <Portal>
        <StyledContent sideOffset={5}>
          {items.map(({ alt, img, rounded, text, link, handleClick }, index) => (
            <div key={index}>
              {bottomLine && index === items.length - 1 && (
                <Line margin={LineMargin.small} />
              )}
              <StyledItem>
                {link ? (
                  <a href={link} target="_blank">
                    {img && (
                      <StyledImage
                        src={img}
                        alt={alt}
                        $rounded={rounded}
                        $size="small"
                      />
                    )}
                    {text}
                  </a>
                ) : (
                  <button onClick={handleClick}>
                    {img && (
                      <StyledImage
                        src={img}
                        alt={alt}
                        $rounded={rounded}
                        $size="small"
                      />
                    )}
                    {text}
                  </button>
                )}
              </StyledItem>
            </div>
          ))}
        </StyledContent>
      </Portal>
    </Root>
  );
}

export default DropDown;
